import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, apiClient } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { Check, ArrowLeft, Loader2, Car, Shield, Zap, Users, Clock, FileText, MapPin, Sparkles, Crown } from "lucide-react";

export default function PricingPage() {
  const [isLoading, setIsLoading] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const FLAT_RATE_PRICE = 59;

  useEffect(() => {
    fetchCurrentSubscription();
    
    const sessionId = searchParams.get("session_id");
    const paymentStatus = searchParams.get("payment");
    
    if (sessionId && paymentStatus === "success") {
      pollPaymentStatus(sessionId);
    } else if (paymentStatus === "cancelled") {
      toast.info("Payment cancelled");
    }
  }, [searchParams]);

  const fetchCurrentSubscription = async () => {
    try {
      const response = await apiClient.get("/subscriptions/my");
      setCurrentSubscription(response.data);
    } catch (error) {
      // No subscription found
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 15;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      toast.error("Payment verification timed out. Please refresh the page.");
      setProcessingPayment(false);
      return;
    }

    try {
      const response = await apiClient.get(`/payments/status/${sessionId}`);
      if (response.data.payment_status === "paid") {
        toast.success("Subscription activated successfully!");
        navigate("/employer?payment=success");
      } else if (response.data.payment_status === "pending") {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
      } else {
        toast.error("Payment was not completed. Please try again.");
        setProcessingPayment(false);
      }
    } catch (error) {
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    }
  };

  const handleSubscribe = async () => {
    if (!user) {
      toast.error("Please log in to subscribe");
      navigate("/login");
      return;
    }

    setProcessingPayment(true);
    try {
      const response = await apiClient.post("/payments/checkout", {
        lookup_key: "nemt_pro_monthly",
        origin_url: window.location.origin,
        quantity: 1
      });
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        toast.error("Failed to create checkout session");
        setProcessingPayment(false);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start checkout");
      setProcessingPayment(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!confirm("Are you sure you want to cancel your subscription?")) return;
    
    try {
      await apiClient.post("/subscriptions/cancel");
      toast.success("Subscription cancelled");
      setCurrentSubscription(null);
    } catch (error) {
      toast.error("Failed to cancel subscription");
    }
  };

  const features = [
    { icon: Users, text: "Unlimited drivers" },
    { icon: Car, text: "Unlimited vehicles" },
    { icon: MapPin, text: "Real-time GPS fleet tracking" },
    { icon: Clock, text: "Smart scheduling & dispatch" },
    { icon: FileText, text: "Insurance claim automation" },
    { icon: Zap, text: "Recurring rides scheduling" },
    { icon: Shield, text: "HIPAA-compliant data" },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-cyan-900 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <Button 
            variant="ghost" 
            onClick={() => navigate(user?.role === "employer" ? "/employer" : "/")}
            className="mb-6 text-slate-400 hover:text-white"
            data-testid="back-to-dashboard"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {user?.role === "employer" ? "Back to Dashboard" : "Back Home"}
          </Button>
          
          <div className="flex items-center justify-center gap-2 mb-4">
            <Crown className="w-10 h-10 text-yellow-400" />
          </div>
          <h1 className="text-4xl md:text-5xl font-black text-white mb-4">
            Simple, Flat-Rate Pricing
          </h1>
          <p className="text-lg text-slate-300 max-w-2xl mx-auto">
            One price. Unlimited everything. No per-vehicle fees.
          </p>
        </div>

        {/* Current Subscription Banner */}
        {currentSubscription && (
          <div className="mb-8 flex items-center justify-center gap-3 bg-gradient-to-r from-emerald-500 to-green-500 text-white rounded-xl px-6 py-4 shadow-lg">
            <Check className="w-5 h-5" />
            <span className="font-medium">
              Active Subscription: NEMT Pro
            </span>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={handleCancelSubscription}
              className="text-white/90 hover:text-white hover:bg-white/20 ml-4"
              data-testid="cancel-subscription-btn"
            >
              Cancel
            </Button>
          </div>
        )}

        {/* Pricing Card */}
        <Card className="bg-white/10 backdrop-blur-lg border-white/20 shadow-2xl overflow-hidden">
          <CardContent className="p-8 md:p-12">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              {/* Left: Price */}
              <div className="text-center md:text-left">
                <div className="inline-flex items-center gap-2 bg-gradient-to-r from-cyan-500/20 to-purple-500/20 border border-cyan-500/30 rounded-full px-4 py-2 mb-6">
                  <Sparkles className="w-4 h-4 text-yellow-400" />
                  <span className="text-cyan-300 text-sm font-medium">Flat Rate - No Per-Vehicle Fees</span>
                </div>
                
                <div className="mb-6">
                  <span className="text-6xl md:text-7xl font-black text-white">${FLAT_RATE_PRICE}</span>
                  <span className="text-slate-400 text-xl">/month</span>
                </div>

                <p className="text-slate-300 mb-8">
                  Complete NEMT fleet management. Unlimited drivers, unlimited vehicles, unlimited rides.
                </p>

                {/* Highlight Box */}
                <div className="bg-gradient-to-br from-cyan-500/10 to-purple-500/10 border border-white/10 rounded-2xl p-6 mb-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-purple-500 flex items-center justify-center">
                      <Crown className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h4 className="text-white font-bold text-lg">NEMT Pro</h4>
                      <p className="text-slate-400 text-sm">Everything unlimited</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-2xl font-bold text-cyan-400">∞</p>
                      <p className="text-slate-400 text-xs">Drivers</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-purple-400">∞</p>
                      <p className="text-slate-400 text-xs">Vehicles</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-orange-400">∞</p>
                      <p className="text-slate-400 text-xs">Rides</p>
                    </div>
                  </div>
                </div>

                <Button
                  onClick={handleSubscribe}
                  disabled={processingPayment || currentSubscription}
                  className="w-full py-6 text-lg font-bold bg-gradient-to-r from-cyan-500 to-purple-500 hover:from-cyan-400 hover:to-purple-400 text-white rounded-xl shadow-lg"
                  data-testid="subscribe-btn"
                >
                  {processingPayment ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : currentSubscription ? (
                    "Already Subscribed"
                  ) : (
                    "Subscribe Now - $59/month"
                  )}
                </Button>

                <p className="text-slate-500 text-sm mt-4 text-center">
                  Cancel anytime. No long-term contracts.
                </p>
              </div>

              {/* Right: Features */}
              <div>
                <h3 className="text-xl font-bold text-white mb-6">Everything included:</h3>
                <div className="space-y-4">
                  {features.map((feature, i) => (
                    <div key={i} className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500/20 to-purple-500/20 flex items-center justify-center">
                        <feature.icon className="w-5 h-5 text-cyan-400" />
                      </div>
                      <span className="text-slate-200">{feature.text}</span>
                    </div>
                  ))}
                </div>

                <div className="mt-8 p-4 bg-amber-500/10 border border-amber-500/20 rounded-xl">
                  <p className="text-amber-200 text-sm">
                    <strong>14-Day Free Trial Available</strong> — Start your trial from the dashboard before subscribing.
                  </p>
                </div>

                <div className="mt-4 p-4 bg-green-500/10 border border-green-500/20 rounded-xl">
                  <p className="text-green-200 text-sm">
                    <strong>Secure Payments</strong> — Powered by Stripe. Your payment info is never stored on our servers.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* FAQ or Additional Info */}
        <div className="mt-12 text-center">
          <p className="text-slate-400">
            Questions? <a href="mailto:support@thereyouare.com" className="text-cyan-400 hover:underline">Contact our team</a>
          </p>
        </div>
      </div>
    </div>
  );
}
